import React, { useRef, useState, useEffect } from "react";
import { 
  Gamepad2, 
  Search, 
  Filter,
  RotateCcw, 
  Download,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye
} from "lucide-react";
import GameActivationModal from "./components/GameActivationModal.jsx";
import GameReceipt from "./components/GameReceipt";
import ViewGameOrderModal from "./components/ViewGameOrderModal.jsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import gameActivationService from "../../../services/gameActivationService";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function GameActivation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    startDate: "",
    endDate: "",
    receiver: "",
    minAmount: "",
    maxAmount: "",
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const closeActivationModal = () => setActivationModalOpen(false);

  const { 
    data, 
    isLoading, 
    isError, 
    error, 
    refetch: refetchData,
    isFetching 
  } = useQuery({
    queryKey: ["gameOrders", filter, refetchTrigger, userInfo?.uid],
    queryFn: async () => {
      const response = await gameActivationService.getGameOrders(filter);
      return response;
    },
    enabled: !!userInfo?.uid,
    retry: 2,
    retryDelay: 1000,
  });

  const handleRefetch = () => {
    setRefetchTrigger(prev => !prev);
    refetchData();
    toast.success(t('gameActivation.toasts.refreshSuccess'));
  };

  const handleActivationSuccess = () => {
    setActivationModalOpen(false);
    handleRefetch();
    toast.success(t('gameActivation.toasts.activationSuccess'));
  };

  const handleSearchChange = (value) => {
    setFilter(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilter(prev => ({ 
      ...prev, 
      status: status === "all" ? "" : status,
      page: 1 
    }));
  };

  const handleDateFilter = (start, end) => {
    setFilter(prev => ({
      ...prev,
      startDate: start || "",
      endDate: end || "",
      page: 1
    }));
  };



  const handleClearFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      status: "",
      startDate: "",
      endDate: "",
      receiver: "",
      minAmount: "",
      maxAmount: "",
      sortBy: "createdAt",
      sortOrder: "DESC",
    });
    setShowFilters(false);
    toast.info(t('gameActivation.toasts.filtersCleared'));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: { 
        bg: "bg-green-100 dark:bg-green-900/30", 
        text: "text-green-800 dark:text-green-200", 
        icon: CheckCircle, 
        label: t('gameActivation.status.completed')
      },
      pending: { 
        bg: "bg-yellow-100 dark:bg-yellow-900/30", 
        text: "text-yellow-800 dark:text-yellow-200", 
        icon: Clock, 
        label: t('gameActivation.status.pending')
      },
      processing: { 
        bg: "bg-secondary-100 dark:bg-secondary-900/30", 
        text: "text-secondary-800 dark:text-secondary-200", 
        icon: RefreshCw, 
        label: t('gameActivation.status.processing')
      },
      failed: { 
        bg: "bg-red-100 dark:bg-red-900/30", 
        text: "text-red-800 dark:text-red-200", 
        icon: XCircle, 
        label: t('gameActivation.status.failed')
      },
      cancelled: { 
        bg: "bg-gray-100 dark:bg-gray-700", 
        text: "text-gray-800 dark:text-gray-200", 
        icon: AlertCircle, 
        label: t('gameActivation.status.cancelled')
      }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${config.bg} ${config.text}`}>
        <Icon size={14} />
        {config.label}
      </div>
    );
  };

  const columns = [
    { 
      key: "txnNumber", 
      label: t('gameActivation.columns.transactionId'),
      sortable: true,
      render: (item) => (
        <div className="font-mono text-sm ">
          {item?.txnNumber || "-"}
        </div>
      )
    },
    { 
      key: "receiver", 
      label: t('gameActivation.columns.gameAccount'),
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
            <Gamepad2 size={16} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <div className="font-medium truncate max-w-[150px] dark:text-white">
              {item?.receiver || "-"}
            </div>
            {item?.operator && (
              <div className="text-xs text-gray-500 dark:text-gray-400">{item.operator}</div>
            )}
          </div>
        </div>
      )
    },
    { 
      key: "totalAmount", 
      label: t('gameActivation.columns.amount'),
      sortable: true,
      render: (item) => (
        <div className="font-semibold text-green-600 dark:text-green-400">
          {parseFloat(item?.totalAmount || item?.amount || 0).toFixed(2)} AFN
        </div>
      )
    },
    {
      key: "product",
      label: t('gameActivation.columns.gameProduct'),
      render: (item) => {
        const productName = item?.productDetails?.productName;
        return (
          <div className="max-w-[180px]">
            <div className="font-medium truncate dark:text-white">
              {typeof productName === 'object' 
                ? (productName?.en || productName?.ar || "-")
                : (productName || "-")}
            </div>
            {item?.productDetails?.description && (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {typeof item.productDetails.description === 'object'
                  ? (item.productDetails.description.en || item.productDetails.description.ar)
                  : item.productDetails.description}
              </div>
            )}
          </div>
        );
      }
    },
  
    {
      key: "status",
      label: t('gameActivation.columns.status'),
      sortable: true,
      render: (item) => getStatusBadge(item?.status)
    },
    {
      key: "createdAt",
      label: t('gameActivation.columns.createdAt'),
      sortable: true,
      render: (item) => (
        <div className="text-sm">
          <div className="font-medium dark:text-white">
            {item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {item?.createdAt ? new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
          </div>
        </div>
      )
    },
    {
      key: "actions",
      label: t('gameActivation.columns.actions'),
      render: (item) => (
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedOrder(item);
              setReceiptOpen(true);
            }}
            className="text-secondary-600 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-secondary-300 p-1.5 border border-secondary-600 dark:border-secondary-400 rounded hover:bg-secondary-50 dark:hover:bg-secondary-900/30 transition-colors"
            title={t('gameActivation.actions.viewReceipt')}
          >
            <Eye size={16} />
          </button>
        </div>
      )
    }
  ];

  const responseData = data?.data?.data;
  const orders = responseData?.orders || [];
  const pagination = responseData?.pagination || {
    totalItems: 0,
    totalPages: 1,
    currentPage: filter.page,
    itemsPerPage: filter.limit
  };

  const summaryStats = {
    total: orders.length,
    succeeded: orders.filter(o => o?.status?.toLowerCase() === 'succeeded').length,
    pending: orders.filter(o => o?.status?.toLowerCase() === 'pending').length,
    totalAmount: orders.reduce((sum, o) => sum + parseFloat(o?.totalAmount || o?.amount || 0), 0).toFixed(2),
    avgAmount: orders.length > 0 
      ? (orders.reduce((sum, o) => sum + parseFloat(o?.totalAmount || o?.amount || 0), 0) / orders.length).toFixed(2) 
      : "0.00"
  };

  const customEmptyState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className="relative">
        <div className="w-40 h-40 flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full">
          <Gamepad2 size={64} className="text-gray-400 dark:text-gray-500" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
          <Gamepad2 size={20} className="text-primary-600 dark:text-primary-400" />
        </div>
      </div>
      <p className="mt-6 text-lg font-medium text-gray-700 dark:text-white">{t('gameActivation.emptyState.title')}</p>
      <p className="mt-2 text-gray-500 dark:text-gray-400 text-center max-w-md">
        {t('gameActivation.emptyState.description')}
      </p>
      <button
        onClick={() => setActivationModalOpen(true)}
        className="mt-6 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg hover:from-primary-700 hover:to-secondary-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
      >
        <Gamepad2 size={18} />
        {t('gameActivation.emptyState.button')}
      </button>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className="w-32 h-32 flex items-center justify-center bg-red-50 dark:bg-red-900/20 rounded-full">
        <Gamepad2 size={48} className="text-red-400 dark:text-red-500" />
      </div>
      <p className="mt-6 text-lg font-medium text-gray-700 dark:text-white">{t('gameActivation.errorState.title')}</p>
      <div className="mt-2 text-sm text-red-600 dark:text-red-400 mb-4 text-center max-w-md">
        {error?.response?.data?.message || error?.message || t('gameActivation.errorState.message')}
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleRefetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
        >
          <RotateCcw size={16} />
          {t('gameActivation.errorState.retry')}
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 dark:text-gray-300"
        >
          {t('gameActivation.errorState.refreshPage')}
        </button>
      </div>
    </div>
  );

  if (!userInfo?.uid) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">{t('gameActivation.loading.userInfo')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      <GameActivationModal 
        open={activationModalOpen} 
        onClose={closeActivationModal} 
        onSuccess={handleActivationSuccess} 
      />
      
      <GameReceipt
        transaction={selectedOrder} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />

      <ViewGameOrderModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />
      
   
      <div className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{t('gameActivation.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300">{t('gameActivation.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
      
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-secondary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-700 dark:text-primary-300">{t('gameActivation.stats.totalActivations')}</p>
              <p className="text-2xl font-bold text-primary-900 dark:text-primary-200">{summaryStats.total}</p>
            </div>
            <div className="w-12 h-12 bg-primary-200 dark:bg-primary-600 rounded-full flex items-center justify-center">
              <Gamepad2 size={24} className="text-primary-600 dark:text-primary-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">{t('gameActivation.stats.successful')}</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-200">{summaryStats.succeeded}</p>
            </div>
            <div className="w-12 h-12 bg-green-200 dark:bg-green-800 rounded-full flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600 dark:text-green-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">{t('gameActivation.stats.pending')}</p>
              <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{summaryStats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-200 dark:bg-yellow-800 rounded-full flex items-center justify-center">
              <Clock size={24} className="text-yellow-600 dark:text-yellow-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 dark:from-secondary-900/20 dark:to-secondary-900/20 border border-secondary-200 dark:border-secondary-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-700 dark:text-secondary-300">{t('gameActivation.stats.totalAmount')}</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-200">{summaryStats.totalAmount} AFN</p>
            </div>
            <div className="w-12 h-12 bg-secondary-200 dark:bg-secondary-800 rounded-full flex items-center justify-center">
              <DollarSign size={24} className="text-secondary-600 dark:text-secondary-300" />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mt-6">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="max-w-[400px] w-full h-[44px] relative rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800">
            <input
              ref={searchRef}
              onChange={(e) => {
                handleSetSearch(handleSearchChange, e.target.value, searchRef);
              }}
              type="text"
              placeholder={t('gameActivation.search.placeholder')}
              defaultValue={filter.search}
              className="bg-transparent h-full pl-10 pr-3 py-2 w-full outline-none dark:text-white dark:placeholder-gray-400"
            />
            <Search size={18} className="absolute top-[13px] left-3 text-gray-400 dark:text-gray-500" />
            {filter.search && (
              <button
                onClick={() => handleSearchChange("")}
                className="absolute top-[12px] right-3 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
              >
                ×
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 text-sm flex items-center gap-2 whitespace-nowrap dark:text-gray-300"
          >
            <Filter size={16} />
            {t('gameActivation.filters.title')}
            {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC" && v !== "").length > 2 && (
              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC" && v !== "").length - 2}
              </span>
            )}
          </button>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={handleRefetch}
            disabled={isFetching}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 text-sm flex items-center gap-2 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? t('gameActivation.buttons.refreshing') : t('gameActivation.buttons.refresh')}
          </button>
          <button
            onClick={() => setActivationModalOpen(true)}
            className="bg-gradient-to-r from-primary-600 to-secondary-600 px-4 py-2 rounded-md text-white text-sm hover:from-primary-700 hover:to-secondary-700 flex items-center gap-2"
          >
            <Gamepad2 size={16} />
            {t('gameActivation.buttons.activate')}
          </button>
        </div>
      </div>

 
      {showFilters && (
        <div className="mt-4 p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-800 dark:text-white">{t('gameActivation.filters.advancedFilters')}</h3>
            <button
              onClick={handleClearFilters}
              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              {t('gameActivation.filters.clearAll')}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('gameActivation.filters.status')}</label>
              <select
                value={filter.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-700 dark:text-white"
              >
                <option value="">{t('gameActivation.filters.allStatus')}</option>
                <option value="succeeded">{t('gameActivation.status.completed')}</option>
                <option value="pending">{t('gameActivation.status.pending')}</option>
                <option value="processing">{t('gameActivation.status.processing')}</option>
                <option value="failed">{t('gameActivation.status.failed')}</option>
                <option value="cancelled">{t('gameActivation.status.cancelled')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('gameActivation.filters.dateRange')}</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filter.startDate}
                  onChange={(e) => handleDateFilter(e.target.value, filter.endDate)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-700 dark:text-white"
                />
                <input
                  type="date"
                  value={filter.endDate}
                  onChange={(e) => handleDateFilter(filter.startDate, e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-700 dark:text-white"
                />
              </div>
            </div>

      
          </div>
        </div>
      )}

      <div className="mt-6">
        <ARHCustomDataTable
          data={orders}
          columns={columns}
          meta={{
            page: pagination.currentPage || filter.page,
            pages: pagination.totalPages || 1,
            total: pagination.totalItems || orders.length,
            limit: pagination.itemsPerPage || filter.limit
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName="hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
          headerClassName="bg-gray-50 dark:bg-slate-700 dark:text-white"
          showPagination={true}
          showEntries={true}
          showSearch={false}
        />
      </div>
    </div>
  );
}

export default GameActivation;